// ============================================
// File: src/components/Gallery.js
// ============================================
import React, { useState } from 'react';
import ImageModal from './ImageModal';

const importAll = (r) => {
  let images = {};
  r.keys().forEach((item) => {
    const match = item.match(/\/([^\/]+)\.jpg$/);
    if (match) {
      images[match[1]] = r(item);
    }
  });
  return images;
};

const allImages = importAll(require.context('../assets', false, /\.jpg$/));

const categoryShortCodes = {
  'مشاريع إلكترونيات': 'el-proj',
  'مخلفات طبية': 'med-waste',
  'معدات تدوير': 'rec-equip',
  'فرز المخلفات': 'sort',
  'نفايات الاتصالات': 'tel-waste',
  'ورش العمل': 'workshop',
  'الاستدامة': 'sustain'
};

const generatePlaceholder = (shortCode, index, category) => {
  const getIcon = () => {
    const icons = {
      'el-proj': '💻',
      'med-waste': '🏥',
      'rec-equip': '♻️',
      'sort': '🗑️',
      'tel-waste': '📱',
      'workshop': '🔧',
      'sustain': '🌱'
    };
    return icons[shortCode] || '📷';
  };
  
  const icon = getIcon();
  const categoryName = encodeURIComponent(category);
  
  return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600' viewBox='0 0 800 600'%3E
    %3Cdefs%3E
      %3ClinearGradient id='g' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E
        %3Cstop offset='0%25' style='stop-color:%2353b096'/%3E
        %3Cstop offset='100%25' style='stop-color:%2371b887'/%3E
      %3C/linearGradient%3E
      %3Cpattern id='p' width='40' height='40' patternUnits='userSpaceOnUse'%3E
        %3Ccircle cx='20' cy='20' r='1.5' fill='rgba(255,255,255,0.1)'/%3E
      %3C/pattern%3E
    %3C/defs%3E
    %3Crect width='800' height='600' fill='url(%23g)'/%3E
    %3Crect width='800' height='600' fill='url(%23p)'/%3E
    %3Ctext x='400' y='280' font-family='Segoe UI, Arial' font-size='80' fill='white' text-anchor='middle' dominant-baseline='middle' opacity='0.9'%3E${icon}%3C/text%3E
    %3Ctext x='400' y='360' font-family='Cairo, Segoe UI, Arial' font-size='24' fill='white' text-anchor='middle' dominant-baseline='middle' font-weight='bold'%3E${categoryName}%3C/text%3E
    %3Ctext x='400' y='400' font-family='Cairo, Segoe UI, Arial' font-size='18' fill='rgba(255,255,255,0.85)' text-anchor='middle' dominant-baseline='middle'%3E${index}%3C/text%3E
    %3Crect x='320' y='440' width='160' height='36' rx='18' fill='rgba(255,255,255,0.15)' stroke='rgba(255,255,255,0.3)' stroke-width='1'/%3E
    %3Ctext x='400' y='463' font-family='Cairo, Segoe UI, Arial' font-size='13' fill='white' text-anchor='middle' dominant-baseline='middle'%3E✧ قيد التحضير ✧%3C/text%3E
  %3C/svg%3E`.replace(/\s+/g, ' ');
};

const generateGalleryImages = () => {
  const categories = Object.keys(categoryShortCodes);
  const images = [];
  let imageCounter = 1;
  
  for (const category of categories) {
    const shortCode = categoryShortCodes[category];
    let imagesPerCategory = 0;
    
    for (let i = 1; i <= 20; i++) {
      const imageName = `${shortCode}-${i}`;
      if (allImages[imageName]) {
        imagesPerCategory++;
      } else {
        break;
      }
    }
    
    if (imagesPerCategory === 0) {
      imagesPerCategory = category === 'مشاريع إلكترونيات' ? 8 : 7;
    }
    
    for (let i = 1; i <= imagesPerCategory; i++) {
      const imageName = `${shortCode}-${i}`;
      const imageUrl = allImages[imageName] || null;
      const finalImageUrl = imageUrl || generatePlaceholder(shortCode, i, category);
      
      images.push({
        id: imageCounter,
        url: finalImageUrl,
        titleAr: `${category} - مشروع ${i}`,
        titleEn: `${category} - Project ${i}`,
        category: category,
        shortCode: shortCode,
        imageName: imageName,
      });
      imageCounter++;
    }
  }
  
  return images;
};

const Gallery = ({ currentLang }) => {
  const [images] = useState(generateGalleryImages());
  const [selectedImage, setSelectedImage] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [visibleCount, setVisibleCount] = useState(8);

  const translations = {
    ar: {
      title: "معرض المشاريع",
      subtitle: "ألبوم صور يضم مجموعة متنوعة من مشاريعنا ومعداتنا",
      filterAll: "الكل",
      loadMore: "إظهار المزيد",
      showLess: "إظهار أقل",
      close: "إغلاق",
      noResults: "لا توجد نتائج",
      noImagesInCategory: "لا توجد صور في هذا التصنيف",
      imagesRemaining: "صور متبقية",
      categories: {
        'مشاريع إلكترونيات': 'مشاريع إلكترونيات',
        'مخلفات طبية': 'مخلفات طبية',
        'معدات تدوير': 'معدات تدوير',
        'فرز المخلفات': 'فرز المخلفات',
        'نفايات الاتصالات': 'نفايات الاتصالات',
        'ورش العمل': 'ورش العمل',
        'الاستدامة': 'الاستدامة'
      }
    },
    en: {
      title: "Projects Gallery",
      subtitle: "A photo album featuring diverse images of our projects and equipment",
      filterAll: "All",
      loadMore: "Load More",
      showLess: "Show Less",
      close: "Close",
      noResults: "No results found",
      noImagesInCategory: "No images in this category",
      imagesRemaining: "images remaining",
      categories: {
        'مشاريع إلكترونيات': 'Electronics Projects',
        'مخلفات طبية': 'Medical Waste',
        'معدات تدوير': 'Recycling Equipment',
        'فرز المخلفات': 'Waste Sorting',
        'نفايات الاتصالات': 'Telecom Waste',
        'ورش العمل': 'Workshops',
        'الاستدامة': 'Sustainability'
      }
    }
  };

  const t = translations[currentLang];
  const categories = ['all', ...new Set(images.map(img => img.category))];
  
  const filteredImages = images.filter(img => {
    const matchesCategory = filterCategory === 'all' || img.category === filterCategory;
    return matchesCategory;
  });

  const visibleImages = filteredImages.slice(0, visibleCount);
  const hasMoreImages = visibleCount < filteredImages.length;
  const isShowingAll = visibleCount >= filteredImages.length;

  const handleLoadMore = () => {
    setVisibleCount(prevCount => Math.min(prevCount + 8, filteredImages.length));
  };

  const handleShowLess = () => {
    setVisibleCount(8);
    window.scrollTo({ top: document.getElementById('gallery').offsetTop - 100, behavior: 'smooth' });
  };

  const handleFilterChange = (category) => {
    setFilterCategory(category);
    setVisibleCount(8);
  };

  const getDisplayTitle = (image) => {
    return currentLang === 'ar' ? image.titleAr : image.titleEn;
  };

  return (
    <section id="gallery" className="gallery-section">
      <h2 className="section-title">{t.title}</h2>
      <p className="section-sub">{t.subtitle}</p>
      
      <div className="gallery-controls">
        <div className="filter-buttons">
          {categories.map(cat => (
            <button
              key={cat}
              className={`filter-btn ${filterCategory === cat ? 'active' : ''}`}
              onClick={() => handleFilterChange(cat)}
            >
              {cat === 'all' ? t.filterAll : (t.categories[cat] || cat)}
            </button>
          ))}
        </div>
      </div>
      
      <div className="gallery-grid">
        {visibleImages.map((image, index) => (
          <div 
            key={image.id} 
            className="gallery-item"
            onClick={() => setSelectedImage(image)}
            style={{ animationDelay: `${index * 0.03}s` }}
          >
            <div className="gallery-image-wrapper">
              <img 
                src={image.url} 
                alt={getDisplayTitle(image)}
                loading="lazy"
                onError={(e) => {
                  e.target.src = generatePlaceholder(image.shortCode, image.id, image.category);
                }}
              />
              <div className="gallery-overlay">
                <div className="overlay-content">
                  <h4>{getDisplayTitle(image)}</h4>
                  <p>{t.categories[image.category] || image.category}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {filteredImages.length > 8 && (
        <div className="load-more-container">
          {hasMoreImages && !isShowingAll && (
            <button className="load-more-btn" onClick={handleLoadMore}>
              <i className="fas fa-plus-circle"></i>
              <span>{t.loadMore} ({filteredImages.length - visibleCount} {t.imagesRemaining})</span>
            </button>
          )}
          
          {visibleCount > 8 && (
            <button className="show-less-btn" onClick={handleShowLess}>
              <i className="fas fa-minus-circle"></i>
              <span>{t.showLess}</span>
            </button>
          )}
        </div>
      )}
      
      {filteredImages.length === 0 && (
        <div className="no-results">
          <i className="fas fa-search"></i>
          <h3>{t.noResults}</h3>
          <p>{t.noImagesInCategory}</p>
        </div>
      )}
      
      {selectedImage && (
        <ImageModal 
          image={selectedImage} 
          onClose={() => setSelectedImage(null)}
          currentLang={currentLang}
        />
      )}
      
      <style>{`
        .gallery-section {
          padding: 4rem 5%;
          background: transparent;
        }
        
        .section-title {
          text-align: center;
          font-size: 2.5rem;
          color: #1a2a3a;
          margin-bottom: 1rem;
          font-weight: 700;
        }
        
        .section-sub {
          text-align: center;
          color: #6a7a8a;
          font-size: 1.1rem;
          margin-bottom: 2rem;
        }
        
        .gallery-controls {
          display: flex;
          justify-content: center;
          align-items: center;
          flex-wrap: wrap;
          gap: 1.5rem;
          margin-bottom: 2rem;
        }
        
        .filter-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 0.8rem;
          justify-content: center;
        }
        
        .filter-btn {
          padding: 0.6rem 1.2rem;
          border-radius: 50px;
          border: 2px solid rgba(83, 176, 150, 0.3);
          background: white;
          color: #1a2a3a;
          cursor: pointer;
          transition: all 0.3s ease;
          font-family: 'Cairo', sans-serif;
          font-size: 0.9rem;
          font-weight: 500;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
        }
        
        .filter-btn:hover {
          border-color: #53b096;
          background: rgba(83, 176, 150, 0.05);
          transform: translateY(-2px);
        }
        
        .filter-btn.active {
          background: linear-gradient(95deg, #53b096, #71b887);
          border-color: transparent;
          color: white;
          font-weight: 600;
          box-shadow: 0 4px 12px rgba(83, 176, 150, 0.25);
        }
        
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 1.8rem;
          margin-top: 2rem;
        }
        
        .gallery-item {
          cursor: pointer;
          animation: fadeInUp 0.5s ease forwards;
          opacity: 0;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(25px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .gallery-image-wrapper {
          position: relative;
          border-radius: 20px;
          overflow: hidden;
          aspect-ratio: 4 / 3;
          background: #f0f2f5;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }
        
        .gallery-item:hover .gallery-image-wrapper {
          transform: translateY(-5px);
          box-shadow: 0 15px 35px rgba(83, 176, 150, 0.15);
        }
        
        .gallery-image-wrapper img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }
        
        .gallery-item:hover .gallery-image-wrapper img {
          transform: scale(1.08);
        }
        
        .gallery-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(83, 176, 150, 0.92), rgba(113, 184, 135, 0.92));
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: opacity 0.35s ease;
        }
        
        .gallery-item:hover .gallery-overlay {
          opacity: 1;
        }
        
        .overlay-content {
          text-align: center;
          padding: 1.5rem;
          transform: translateY(20px);
          transition: transform 0.35s ease;
        }
        
        .gallery-item:hover .overlay-content {
          transform: translateY(0);
        }
        
        .overlay-content h4 {
          color: white;
          font-size: 1.1rem;
          margin-bottom: 0.5rem;
          font-weight: 700;
        }
        
        .overlay-content p {
          color: rgba(255, 255, 255, 0.9);
          font-size: 0.85rem;
          margin-bottom: 0;
        }
        
        .load-more-container {
          display: flex;
          justify-content: center;
          gap: 1rem;
          margin-top: 3rem;
          flex-wrap: wrap;
        }
        
        .load-more-btn, .show-less-btn {
          padding: 0.9rem 2rem;
          border-radius: 50px;
          font-family: 'Cairo', sans-serif;
          font-weight: 700;
          font-size: 1rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.8rem;
          transition: all 0.3s ease;
          border: none;
        }
        
        .load-more-btn {
          background: linear-gradient(95deg, #53b096, #71b887);
          color: white;
          box-shadow: 0 4px 15px rgba(83, 176, 150, 0.3);
        }
        
        .load-more-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(83, 176, 150, 0.4);
        }
        
        .show-less-btn {
          background: rgba(83, 176, 150, 0.1);
          color: #53b096;
          border: 2px solid rgba(83, 176, 150, 0.3);
        }
        
        .show-less-btn:hover {
          background: rgba(83, 176, 150, 0.2);
          transform: translateY(-2px);
        }
        
        .no-results {
          text-align: center;
          padding: 4rem;
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border-radius: 24px;
          margin-top: 2rem;
          border: 1px solid rgba(83, 176, 150, 0.2);
        }
        
        .no-results i {
          font-size: 3rem;
          color: #53b096;
          margin-bottom: 1rem;
        }
        
        .no-results h3 {
          color: #1a2a3a;
          margin-bottom: 0.5rem;
        }
        
        .no-results p {
          color: #6a7a8a;
        }
        
        @media (max-width: 768px) {
          .gallery-section {
            padding: 2rem 5%;
          }
          
          .section-title {
            font-size: 1.8rem;
          }
          
          .filter-buttons {
            justify-content: center;
          }
          
          .gallery-grid {
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 1.2rem;
          }
          
          .load-more-btn, .show-less-btn {
            padding: 0.7rem 1.5rem;
            font-size: 0.9rem;
          }
        }
        
        @media (max-width: 480px) {
          .gallery-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
};

export default Gallery;